import * as React from 'react';
import { MenuItem, Tooltip } from 'vet-bones/design-system/components';
import { useIsMounted } from 'vet-bones/utils/hooks';

import { useDownloadSdu } from 'src/ui/containers/endpoints/utils/useDownloadSdu';
import { useSyncSdu } from 'src/ui/containers/endpoints/utils/useSyncSdu';
import { Connection } from 'src/ui/graph/types';
import * as copy from 'src/ui/templates/copy';

interface SduMenuActionsProps {
  canReadLogs: boolean;
  connection: Connection;
  downloadTestId: string;
  isSyncDisabled: boolean;
  isSyncInProgress: boolean;
  syncTestId: string;
}

// Shared Download/Sync SDU menu items, rendered inside the host's <MenuItems>.
// Used by the endpoint table rows and the SDU dashboard endpoint action menu so
// the action gating, tooltips, and sync-in-flight handling stay in one place.
export const SduMenuActions: React.VFC<SduMenuActionsProps> = ({
  canReadLogs,
  connection,
  downloadTestId,
  isSyncDisabled,
  isSyncInProgress,
  syncTestId,
}) => {
  const isMounted = useIsMounted();
  const downloadSdu = useDownloadSdu(connection);
  const syncSdu = useSyncSdu(connection);
  const [isSyncingSdu, setIsSyncingSdu] = React.useState(false);

  const handleSyncSdu = () => {
    setIsSyncingSdu(true);
    syncSdu().finally(() => {
      if (isMounted.current) {
        setIsSyncingSdu(false);
      }
    });
  };

  return (
    <>
      <MenuItem
        leftIcon={null}
        onClick={() => downloadSdu()}
        testId={downloadTestId}
        text={copy.components.endpoints.endpointTable.actions.downloadSdu}
      />
      <Tooltip
        description={
          (!canReadLogs &&
            copy.components.endpoints.endpointTable.actions.syncSduPermissionsTooltip(
              connection.name
            )) ||
          (isSyncInProgress &&
            copy.components.endpoints.endpointTable.actions.syncSduTooltip) ||
          ''
        }
        disabled={Boolean(canReadLogs && !isSyncInProgress)}
      >
        <MenuItem
          disabled={
            !canReadLogs || isSyncDisabled || isSyncingSdu || isSyncInProgress
          }
          leftIcon={null}
          onClick={handleSyncSdu}
          testId={syncTestId}
          text={copy.components.endpoints.endpointTable.actions.syncSdu}
        />
      </Tooltip>
    </>
  );
};
